export type LabelTranslationKey =
  | 'A4_STANDARD'
  | 'A4_STANDARD_ECO'
  | 'A4_STANDARD_PARCELS_ON_SEPARATE_PAGES'
  | 'A4_STICKERS_TWO_PER_PAGE'
  | 'A4_STICKERS_FOUR_PER_PAGE'
  | 'FOUR_BY_SIX'
  | 'UNKNOWN';

export const labelTranslationMap: { [key in LabelTranslationKey]: string } = {
  A4_STANDARD: 'A4 Standard\nPrinter',
  A4_STANDARD_ECO: 'A4 Standard\n(Multiple Labels per Page)',
  A4_STANDARD_PARCELS_ON_SEPARATE_PAGES: 'A4 Printer\nParcels on Separate Pages',
  A4_STICKERS_TWO_PER_PAGE: 'A4 Stickers\nTwo per Page',
  A4_STICKERS_FOUR_PER_PAGE: 'A4 Stickers\nFour per Page',
  FOUR_BY_SIX: '4x6 Label\nPrinter',
  UNKNOWN: 'Unknown Label Format'
};

type LabelOption =
  | {
      pageSize: 'A4';
      paperType: 'Standard';
      layout: null;
    }
  // returned when more than one parcel
  | {
      pageSize: 'A4';
      paperType: 'Standard';
      layout: 'Eco';
    }
  // returned when more than one parcel
  | {
      pageSize: 'A4';
      paperType: 'Standard';
      layout: 'ParcelsOnSeparatePages';
    }
  // returned when all non-A4 labels fit on half page
  | {
      pageSize: 'A4';
      paperType: 'Stickers';
      layout: 'TwoPerPage';
    }
  // returned when all non-A4 labels fit on quarter page
  | {
      pageSize: 'A4';
      paperType: 'Stickers';
      layout: 'FourPerPage';
    }
  // returned when _any_ labels fit on 4x6
  | {
      pageSize: 'FourBySix';
      paperType: null;
      layout: null;
    };

export type StatusData = {
  result: {
    orderLineStatuses: { orderLineId: number; status: string }[];
    labelOptions: LabelOption[];
  };
  success: boolean;
  error: object;
};

const upperCaseAndSplitByCapitalLetters = (s: string) => {
  return s
    .replace(/([A-Z])/g, ' $1')
    .toUpperCase()
    .trim()
    .replace(/ /g, '_');
};

export const generateTranslationKey = (type: LabelOption) => {
  let output = type.pageSize;
  if (type.paperType) {
    output += type.paperType;
  }
  if (type.layout) {
    output += type.layout;
  }
  return upperCaseAndSplitByCapitalLetters(output);
};

export const isReady = (d: StatusData) =>
  d?.success && d?.result?.orderLineStatuses?.every((x) => x.status === 'Ready');

export const generateSwrCacheKeyForLink = (link: { Href?: string; Type: string; Meta?: Record<string, any> }) => {
  const output = [link.Href, link.Type];
  if (link.Meta) {
    Object.keys(link.Meta).forEach((k) => {
      output.push(`${k}-${link.Meta[k]}`);
    });
  }
  return output.join('-');
};

export const generateLabelEndpointFromStatusEndpoint = (statusEndpoint: string) => {
  // Example Version Check
  if (statusEndpoint.includes('/v4/')) {
    return statusEndpoint.replace('/status/', '/label/');
  }
  return statusEndpoint.replace('/status/', '/label/');
};

export const generatePresignedLabelMeta = (
  option: LabelOption,
  labelWithPackingSlip: boolean,
  orderId?: string,
  orderLineId?: string,
  orderLineIds?: string[]
) => {
  const meta: Record<string, any> = {};

  if (option.pageSize) {
    meta.pageSize = option.pageSize;
  }

  if (option.paperType) {
    meta.paperType = option.paperType;
  }

  if (option.layout) {
    meta.layout = option.layout;
  }

  if (labelWithPackingSlip) {
    meta.labelDetail = '1';
  }

  if (orderLineId) {
    meta.orderLineId = orderLineId;
  }

  if (orderId) {
    meta.orderId = orderId;
  }

  if (orderLineIds && orderLineIds.length > 0) {
    meta.orderLineIds = orderLineIds;
  }

  return meta;
};
